/**
 * BriefingCommandCenter — `/admin/briefing-command`.
 *
 * Single-page view of every upcoming shift's briefing state, powered by
 * `GET /api/admin/briefing/summary`. Design language matches the dark
 * AI Command Center on JobsV2 (`AiPriorityBriefing`).
 *
 * Status taxonomy (Iter-404):
 *   • not_started            — briefing is empty
 *   • missing_client_info    — some sections, but critical ones missing
 *   • ready_to_send          — every critical section filled
 *   • complete               — every section filled
 *   • (sent_to_talent / partially_acknowledged — Phase 2, reserved)
 *
 * Every card shows the exact missing sections and a one-tap
 * "Ask Client to Complete" button that fires
 * `POST /api/admin/briefing/{id}/request-completion` with 12h dedup.
 */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import {
  Sparkles, ArrowLeft, ClipboardCheck, ClipboardX, ClipboardList,
  Send, ExternalLink, Loader2, RefreshCcw, AlertCircle, CheckCircle2,
} from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Card } from '../../components/ui/card';
import api from '../../utils/api';
import AIBriefingDraftPanel from '../../components/AIBriefingDraftPanel';

const STATUS_META = {
  not_started: {
    label: 'Not Started',       tint: 'rose',
    icon: ClipboardX,
    blurb: 'Briefing is completely empty.',
  },
  missing_client_info: {
    label: 'Missing Client Info', tint: 'amber',
    icon: AlertCircle,
    blurb: 'Some sections filled — critical ones still missing.',
  },
  ready_to_send: {
    label: 'Ready to Send',      tint: 'sky',
    icon: ClipboardList,
    blurb: 'Every critical section is present.',
  },
  complete: {
    label: 'Complete',           tint: 'emerald',
    icon: CheckCircle2,
    blurb: 'Nothing outstanding.',
  },
};

const TINTS = {
  rose:    { chip: 'text-rose-300 bg-rose-500/10 border-rose-500/40',       dot: 'bg-rose-500'   },
  amber:   { chip: 'text-amber-300 bg-amber-500/10 border-amber-500/40',    dot: 'bg-amber-500'  },
  sky:     { chip: 'text-sky-300 bg-sky-500/10 border-sky-500/40',          dot: 'bg-sky-500'    },
  emerald: { chip: 'text-emerald-300 bg-emerald-500/10 border-emerald-500/40', dot: 'bg-emerald-500' },
};

function StatusChip({ status }) {
  const meta = STATUS_META[status];
  if (!meta) return null;
  const t = TINTS[meta.tint];
  return (
    <span
      className={`inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wide border ${t.chip}`}
      data-testid={`bcc-status-${status}`}
    >
      <span className={`w-1.5 h-1.5 rounded-full ${t.dot}`} />
      {meta.label}
    </span>
  );
}

function BriefingCard({ analysis, onRequest, busyId, navigate, onOpenAiDraft }) {
  const shiftDate = analysis.shift_date;
  const missing = analysis.missing_sections || [];
  const critical = analysis.critical_missing || [];
  const isBusy = busyId === analysis.shift_id;

  return (
    <div
      className="rounded-xl border border-slate-800/70 bg-slate-900/60 backdrop-blur-sm p-4 flex flex-col gap-3"
      data-testid={`bcc-card-${analysis.shift_id}`}
    >
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2 flex-wrap">
            <StatusChip status={analysis.status} />
            {analysis.next_actor && analysis.next_actor !== 'none' && (
              <span className="text-[10px] uppercase tracking-wide text-slate-400 bg-slate-800/60 border border-slate-700/60 px-1.5 py-0.5 rounded">
                next · {analysis.next_actor}
              </span>
            )}
          </div>
          <div className="font-semibold text-slate-100 mt-1.5 truncate">
            {analysis.event_title || 'Untitled Event'}
          </div>
          {shiftDate && (
            <div className="text-xs text-slate-400 mt-0.5">{shiftDate}</div>
          )}
        </div>
        <div className="text-right shrink-0">
          <div className="text-2xl font-extrabold text-slate-100 tabular-nums">
            {analysis.completeness_pct}%
          </div>
          <div className="text-[10px] uppercase text-slate-500 tracking-wide">complete</div>
        </div>
      </div>

      {missing.length > 0 && (
        <div className="bg-slate-950/60 border border-slate-800/60 rounded-lg p-2.5">
          <div className="text-[10px] uppercase tracking-wide text-slate-400 mb-1">Missing sections</div>
          <div className="flex flex-wrap gap-1">
            {missing.map((m) => (
              <span
                key={m.id}
                className={`text-[11px] px-1.5 py-0.5 rounded border ${
                  m.critical
                    ? 'bg-rose-500/10 text-rose-200 border-rose-500/40'
                    : 'bg-slate-800/60 text-slate-300 border-slate-700/60'
                }`}
              >
                {m.label}
              </span>
            ))}
          </div>
          {critical.length > 0 && (
            <div className="text-[11px] text-rose-300/80 mt-1.5">
              {critical.length} critical
            </div>
          )}
        </div>
      )}

      <div className="flex items-center gap-2 mt-auto flex-wrap">
        {analysis.status !== 'complete' && (
          <Button
            size="sm"
            onClick={() => onRequest(analysis.shift_id)}
            disabled={isBusy}
            className="bg-violet-600 hover:bg-violet-500 border-violet-500 text-white h-8"
            data-testid={`bcc-request-${analysis.shift_id}`}
          >
            {isBusy
              ? <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" />
              : <Send className="w-3.5 h-3.5 mr-1.5" />}
            Ask Client to Complete
          </Button>
        )}
        <Button
          size="sm"
          onClick={() => onOpenAiDraft?.(analysis.shift_id)}
          className="bg-teal-500 hover:bg-teal-400 text-slate-950 h-8 border-teal-400"
          data-testid={`bcc-ai-draft-${analysis.shift_id}`}
        >
          <Sparkles className="w-3.5 h-3.5 mr-1.5" />
          AI Draft
        </Button>
        <Button
          size="sm"
          variant="outline"
          className="h-8 border-slate-700/60 bg-slate-900/40 text-slate-200 hover:bg-slate-800/70"
          onClick={() => navigate(`/admin/shifts/${analysis.shift_id}`)}
          data-testid={`bcc-open-${analysis.shift_id}`}
        >
          Open shift <ExternalLink className="w-3.5 h-3.5 ml-1.5" />
        </Button>
      </div>
    </div>
  );
}

export default function BriefingCommandCenter() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [summary, setSummary] = useState({ count: 0, counts_by_status: {}, analyses: [] });
  const [statusFilter, setStatusFilter] = useState(null);
  const [busyId, setBusyId] = useState(null);
  const [aiDraftShiftId, setAiDraftShiftId] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get('/admin/briefing/summary?limit=200');
      setSummary(data);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to load briefing summary');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const filtered = useMemo(() => {
    if (!statusFilter) return summary.analyses || [];
    return (summary.analyses || []).filter((a) => a.status === statusFilter);
  }, [summary.analyses, statusFilter]);

  const handleRequest = async (shiftId) => {
    setBusyId(shiftId);
    try {
      const { data } = await api.post(`/admin/briefing/${shiftId}/request-completion`);
      if (data.skipped) {
        toast.info(data.reason === 'recently_requested'
          ? 'Already asked the client within the last 12h.'
          : (data.reason || 'Nothing to request.'));
      } else {
        toast.success(data.message || 'Client notified');
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Request failed');
    } finally {
      setBusyId(null);
    }
  };

  return (
    <div className="min-h-screen bg-slate-950 text-slate-100">
      {/* Header band */}
      <div className="relative overflow-hidden border-b border-slate-800/60 bg-gradient-to-br from-slate-950 via-slate-900 to-slate-950">
        <div aria-hidden className="pointer-events-none absolute inset-0 opacity-30"
             style={{
               backgroundImage:
                 'radial-gradient(1px 1px at 20% 20%, rgba(148,163,184,0.14) 1px, transparent 1px), radial-gradient(1px 1px at 80% 60%, rgba(148,163,184,0.12) 1px, transparent 1px)',
               backgroundSize: '32px 32px',
             }}
        />
        <div className="relative max-w-7xl mx-auto px-4 md:px-8 py-5">
          <button
            className="text-xs text-slate-400 hover:text-slate-100 inline-flex items-center gap-1.5"
            onClick={() => navigate('/admin/jobs-v2')}
            data-testid="bcc-back"
          >
            <ArrowLeft className="w-3.5 h-3.5" /> Back to Jobs
          </button>
          <div className="mt-2 flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-violet-500/30 to-sky-500/30 border border-violet-500/40 flex items-center justify-center">
              <ClipboardCheck className="w-5 h-5 text-violet-200" />
            </div>
            <div>
              <div className="text-[10px] uppercase tracking-[0.15em] font-bold text-violet-300/90 flex items-center gap-2">
                <Sparkles className="w-3 h-3" /> AI Briefing Command
              </div>
              <h1 className="text-2xl md:text-3xl font-extrabold tracking-tight text-slate-100">
                Briefings dashboard
              </h1>
              <p className="text-sm text-slate-400">
                {summary.count} upcoming shift{summary.count === 1 ? '' : 's'} monitored ·{' '}
                <span className="text-slate-200">
                  {(summary.counts_by_status?.not_started || 0) +
                   (summary.counts_by_status?.missing_client_info || 0)}
                </span>{' '}
                need client action
              </p>
            </div>
            <Button
              size="sm"
              variant="outline"
              onClick={load}
              className="ml-auto h-8 border-slate-700/60 bg-slate-900/40 text-slate-200 hover:bg-slate-800/70"
              data-testid="bcc-refresh"
            >
              <RefreshCcw className="w-3.5 h-3.5 mr-1.5" /> Refresh
            </Button>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 md:px-8 py-5 space-y-4">
        {/* Status filter chips */}
        <div className="flex flex-wrap gap-2" data-testid="bcc-filter-chips">
          <button
            type="button"
            onClick={() => setStatusFilter(null)}
            className={`px-3 py-1.5 rounded-full text-xs font-semibold border transition ${
              !statusFilter
                ? 'bg-slate-100 text-slate-900 border-slate-100'
                : 'bg-slate-900/40 text-slate-300 border-slate-700/60 hover:border-slate-500'
            }`}
            data-testid="bcc-filter-all"
          >
            All <span className="ml-1 opacity-70">{summary.count || 0}</span>
          </button>
          {Object.keys(STATUS_META).map((s) => {
            const c = summary.counts_by_status?.[s] || 0;
            const meta = STATUS_META[s];
            const t = TINTS[meta.tint];
            const active = statusFilter === s;
            return (
              <button
                key={s}
                type="button"
                onClick={() => setStatusFilter(active ? null : s)}
                className={`px-3 py-1.5 rounded-full text-xs font-semibold border transition inline-flex items-center gap-1.5 ${
                  active ? `${t.chip} ring-2 ring-offset-2 ring-offset-slate-950` :
                  'bg-slate-900/40 text-slate-300 border-slate-700/60 hover:border-slate-500'
                }`}
                data-testid={`bcc-filter-${s}`}
              >
                <span className={`w-1.5 h-1.5 rounded-full ${t.dot}`} />
                {meta.label}
                <span className="text-[10px] font-bold px-1.5 py-0.5 rounded bg-slate-800/60">{c}</span>
              </button>
            );
          })}
        </div>

        {/* Grid */}
        {loading && (
          <div className="text-center py-16 text-slate-400" data-testid="bcc-loading">
            <Loader2 className="w-6 h-6 animate-spin inline mr-2" /> Analyzing briefings…
          </div>
        )}

        {!loading && filtered.length === 0 && (
          <Card className="p-10 text-center bg-slate-900/60 border-slate-800/60 text-slate-400" data-testid="bcc-empty">
            <CheckCircle2 className="w-8 h-8 mx-auto mb-2 text-emerald-400/70" />
            {statusFilter
              ? `No briefings currently in "${STATUS_META[statusFilter].label}".`
              : 'No upcoming shifts in the next 30 days.'}
          </Card>
        )}

        {!loading && filtered.length > 0 && (
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-3" data-testid="bcc-grid">
            {filtered.map((a) => (
              <BriefingCard
                key={a.shift_id}
                analysis={a}
                onRequest={handleRequest}
                busyId={busyId}
                navigate={navigate}
                onOpenAiDraft={setAiDraftShiftId}
              />
            ))}
          </div>
        )}
      </div>

      {aiDraftShiftId && (
        <div className="fixed inset-0 z-40 flex items-center justify-center bg-black/70 p-4">
          <div className="w-full max-w-4xl max-h-[90vh] overflow-y-auto">
            <div className="flex justify-end mb-2">
              <button
                onClick={() => setAiDraftShiftId(null)}
                className="text-slate-300 hover:text-white text-sm bg-slate-800 hover:bg-slate-700 rounded px-3 py-1"
                data-testid="bcc-ai-draft-close"
              >
                Close
              </button>
            </div>
            <AIBriefingDraftPanel
              shiftId={aiDraftShiftId}
              onApproved={() => {
                setAiDraftShiftId(null);
                load();
              }}
            />
          </div>
        </div>
      )}
    </div>
  );
}
